import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Building2, MapPin } from 'lucide-react';
import { ProfileCardProps, getHeaderGradient, getImageUrl } from './types';
import { Card } from '../ui/Card';
import ProfileCardHeader from './ProfileCardHeader';
import ProfileCardPhoto from './ProfileCardPhoto';
import ProfileCardInfo from './ProfileCardInfo';
import ProfileCardContact from './ProfileCardContact';
import ProfileCardSkills from './ProfileCardSkills';
import ProfileCardAchievements from './ProfileCardAchievements';
import ProfileCardSocialLinks from './ProfileCardSocialLinks';

/**
 * Shared ProfileCard component used in both:
 * - ProfileConfigurationCenter (variant="preview")
 * - PublicProfileDisplay (variant="full")
 *
 * This ensures visual consistency between the live preview and actual public display.
 */
const ProfileCard: React.FC<ProfileCardProps> = ({
  data,
  variant,
  showQRCode = false,
  onLeaveContact,
  t
}) => {
  const isPreview = variant === 'preview';

  const handleEmailClick = () => {
    if (data.email) {
      window.location.href = `mailto:${data.email}`;
    }
  };

  const handlePhoneClick = () => {
    if (data.phone) {
      window.location.href = `tel:${data.phone}`;
    }
  };

  if (isPreview) {
    // Preview variant - scaled-down replica matching the full public profile design
    return (
      <Card variant="elevated" className="overflow-hidden">
        <ProfileCardHeader
          colorScheme={data.colorScheme}
          companyLogo={data.companyLogo}
          variant={variant}
          branding={data.branding}
        />

        <div className="px-3 pb-3">
          {/* Photo & Basic Info — same layout primitive as the full variant
              (flex items-end with a negative top-margin equal to half the
              avatar's height) so the avatar straddles the header/content
              boundary 50/50 and the info text aligns to the avatar's
              bottom. The full version works because the 128px avatar is
              taller than the info text; we mirror that here by giving the
              info column `min-h-0` and letting the avatar dominate the
              row. */}
          {/* Constrain the flex row to the avatar's intrinsic 56px height so
              `items-end` actually aligns the info text to the avatar's bottom.
              Without this, the multi-line info column stretches the row,
              dragging the avatar down and breaking the half-in-header
              overlap. The info column overflows visibly. */}
          <div className="flex items-end gap-3 -mt-7 mb-2 relative z-10 h-14">
            <div className="flex-shrink-0">
              <ProfileCardPhoto
                photo={data.photo}
                firstName={data.firstName}
                lastName={data.lastName}
                colorScheme={data.colorScheme}
                variant={variant}
              />
            </div>
            <div className="flex-1 min-w-0">
              <ProfileCardInfo
                fullName={data.fullName}
                jobTitle={data.jobTitle}
                department={data.department}
                location={data.location}
                bio=""
                variant={variant}
                t={t}
              />
            </div>
          </div>

          {/* Bio Section - separate like full version */}
          {data.bio && (
            <div className="mb-2">
              <h4 className="text-[10px] font-semibold text-foreground mb-0.5 uppercase tracking-wide">{t('profileBio')}</h4>
              <p dir="auto" className="text-[10px] text-muted-foreground leading-relaxed line-clamp-2">{data.bio}</p>
            </div>
          )}

          <ProfileCardContact
            email={data.email}
            phone={data.phone}
            showEmail={data.showEmail}
            showPhone={data.showPhone}
            variant={variant}
            onLeaveContact={onLeaveContact}
            t={t}
          />

          <ProfileCardSkills
            skills={data.skills}
            showSkills={data.showSkills}
            colorScheme={data.colorScheme}
            variant={variant}
            branding={data.branding}
            t={t}
          />

          <ProfileCardAchievements
            achievements={data.achievements}
            showAchievements={data.showAchievements}
            variant={variant}
            t={t}
          />

          <ProfileCardSocialLinks
            socialLinks={data.socialLinks}
            variant={variant}
          />

          {/* QR Code Section - Preview */}
          {showQRCode && (
            <div className="mt-2 pt-2 border-t border-border">
              <h4 className="text-[10px] font-semibold text-foreground mb-1 text-center">{t('Scan to Connect')}</h4>
              <div className="flex justify-center">
                <div className="bg-card p-1.5 rounded-lg border border-border shadow-sm">
                  {data.profileUrl ? (
                    <QRCodeSVG
                      value={data.profileUrl}
                      size={60}
                      level="H"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  ) : (
                    <div className="w-[60px] h-[60px] border-2 border-dashed border-border flex items-center justify-center text-[9px] text-muted-foreground">
                      {t('Save to generate')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Full variant - complete card for public profile display.
  // A slim colored banner with the avatar straddling its bottom edge 50/50; the
  // identity (name → job-title pill → department/location) stacks beside the
  // avatar on ≥md and centers below it on phones. The name rides on the
  // colour on desktop (white text) and falls just below the seam on phones, where
  // it renders dark-on-white for legibility.
  const heroGradient = getHeaderGradient(data.colorScheme, data.branding);

  return (
    <Card className="rounded-2xl shadow-lg overflow-hidden">
      {/* Slim colored banner */}
      <div className={`relative ${heroGradient.className} h-24 sm:h-28`} style={heroGradient.style}>
        {/* Company logo — frosted well in the top corner (RTL-aware) */}
        {data.companyLogo && (
          <div className="absolute top-4 ltr:right-4 rtl:left-4">
            <div className="h-11 w-24 sm:w-28 p-1.5 bg-white/15 backdrop-blur-md rounded-md flex items-center justify-center ring-1 ring-white/25 shadow-lg">
              <img
                src={getImageUrl(data.companyLogo) || ''}
                alt=""
                aria-hidden="true"
                className="max-h-full max-w-full object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
              />
            </div>
          </div>
        )}
      </div>

      <div className="px-6 sm:px-8 pb-8">
        {/* Identity — the avatar straddles the seam 50/50 (its -mt-16 lifts the
            row so half sits on the banner, half below). Centered stack on
            phones; avatar-left with the name/title/location column beside it on
            ≥md. */}
        <div className="relative z-10 flex flex-col items-center max-md:text-center md:flex-row md:items-start md:text-start gap-3 md:gap-5 -mt-16 mb-6">
          <div className="shrink-0">
            <ProfileCardPhoto
              photo={data.photo}
              firstName={data.firstName}
              lastName={data.lastName}
              colorScheme={data.colorScheme}
              variant={variant}
            />
          </div>

          <div className="min-w-0 md:pt-2">
            {/* Name. White on the banner (≥md) and dark-on-white on phones.
                NOTE: a "Verified" badge used to sit here. It was dropped
                (owner decision, 2026-08-04) — it rendered from a hardcoded
                value, never from any real verification, so on an anonymous
                public page it asserted something untrue about a named person. */}
            <div className="flex items-center max-md:justify-center md:justify-start gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-semibold max-md:text-foreground md:text-white md:[text-shadow:0_1px_10px_rgba(0,0,0,0.35)]">
                {data.fullName}
              </h1>
            </div>

            {/* Job title — pill directly under the name */}
            {data.jobTitle && (
              <div className="mt-2 flex max-md:justify-center md:justify-start">
                <span className="max-w-full truncate rounded-full bg-muted px-3.5 py-1 text-sm font-medium text-foreground ring-1 ring-border">
                  {data.jobTitle}
                </span>
              </div>
            )}

            {/* Department • Location — under the job title */}
            {(data.department || data.location) && (
              <div className="mt-2 flex items-center max-md:justify-center md:justify-start gap-x-2 text-sm text-muted-foreground">
                {data.department && (
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    {data.department}
                  </span>
                )}
                {data.department && data.location && <span aria-hidden="true">•</span>}
                {data.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {data.location}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bio / About */}
        {data.bio && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-2 uppercase tracking-wide">{t('profileBio')}</h3>
            <p dir="auto" className="text-muted-foreground leading-relaxed whitespace-pre-line">{data.bio}</p>
          </div>
        )}

        <ProfileCardContact
          email={data.email}
          phone={data.phone}
          showEmail={data.showEmail}
          showPhone={data.showPhone}
          variant={variant}
          onLeaveContact={onLeaveContact}
          onEmailClick={handleEmailClick}
          onPhoneClick={handlePhoneClick}
          t={t}
        />

        <ProfileCardSkills
          skills={data.skills}
          showSkills={data.showSkills}
          colorScheme={data.colorScheme}
          variant={variant}
          branding={data.branding}
          t={t}
        />

        <ProfileCardAchievements
          achievements={data.achievements}
          showAchievements={data.showAchievements}
          variant={variant}
          t={t}
        />

        <ProfileCardSocialLinks
          socialLinks={data.socialLinks}
          variant={variant}
        />
      </div>
    </Card>
  );
};

export default ProfileCard;

// Re-export types for convenience
export * from './types';
